import React, { useState, useEffect } from 'react';
import ModalBase from './ModalBase';
import UploadBox from './UploadBox';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { FileFormat } from '../types';
import { useUploadContext } from '../contexts/UploadContext';
import useURLParams from '../hooks/useURLParams';
import {
    fetchUploadToken,
    postPackCopyAutomatic,
} from '../utils/backendServices';
import axios from 'axios';
import '../styles/components/UploadPackCopy.scss';

interface CreatePackCopyAutomaticModalProps {
    onClose: () => void;
    onBack: () => void;
    packCopyData: {
        versionName: string;
        notes: string;
    };
    projectId: string;
}

const CreatePackCopyAutomaticModal: React.FC<
    CreatePackCopyAutomaticModalProps
> = ({ onClose, onBack, packCopyData, projectId }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [canSubmit, setCanSubmit] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const { setParam } = useURLParams();
    const { triggerFetch } = useUploadContext();

    useEffect(() => {
        setCanSubmit(!!selectedFile && !isUploading);
    }, [selectedFile, isUploading]);

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);

        try {
            const fileFormat = selectedFile.name
                .split('.')
                .pop()
                ?.toLowerCase() as FileFormat;

            if (fileFormat && Object.values(FileFormat).includes(fileFormat)) {
                const { url, unique_id } = await fetchUploadToken(
                    fileFormat,
                    packCopyData.versionName,
                    'pack-copy',
                    projectId,
                );

                triggerFetch();

                await axios.put(url, selectedFile, {
                    headers: { 'x-ms-blob-type': 'BlockBlob' },
                });

                let fileConfig;

                if (
                    fileFormat === FileFormat.DOC ||
                    fileFormat === FileFormat.DOCX ||
                    fileFormat === FileFormat.XLS ||
                    fileFormat === FileFormat.XLSX ||
                    fileFormat === FileFormat.CSV ||
                    fileFormat === FileFormat.PDF
                ) {
                    fileConfig = {
                        file_format: fileFormat,
                    };

                    if (fileConfig) {
                        await postPackCopyAutomatic(unique_id, {
                            file_config: fileConfig,
                            properties: {
                                version_name: packCopyData.versionName,
                                notes: packCopyData.notes || undefined,
                                project_id: projectId,
                            },
                        });
                    }

                    setParam('modal', null);
                }
            }
        } catch (error) {
            console.error('Upload failed:', error);
            // TODO: Show error message to user
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <ModalBase onClose={onClose} width="500px">
            <div className="upload-components">
                <h2 className="modal-title">Upload Document</h2>
                <p className="modal-description">
                    Upload your pack copy document. Our AI will automatically
                    extract and organize the specs for you.
                </p>

                <UploadBox
                    setSelectedFile={setSelectedFile}
                    selectedFile={selectedFile}
                    allowImageUpload={false}
                    allowPdfUpload={true}
                    requireSinglePdfPage={false}
                    allowWordUpload={true}
                    allowExcelUpload={true}
                    allowCsvUpload={true}
                    maxFileSizeMB={15}
                    minImageDimension={2000}
                />

                <div className="button-row">
                    <button
                        className="btn-secondary"
                        onClick={onBack}
                        disabled={isUploading}
                    >
                        Back
                    </button>
                    <SignedIn>
                        <button
                            className="btn-primary"
                            onClick={handleUpload}
                            disabled={!canSubmit}
                        >
                            {isUploading ? 'Uploading...' : 'Upload & Process'}
                        </button>
                    </SignedIn>
                    <SignedOut>
                        <SignInButton>
                            <button
                                className="btn-primary"
                                disabled={!canSubmit}
                            >
                                {isUploading
                                    ? 'Uploading...'
                                    : 'Upload & Process'}
                            </button>
                        </SignInButton>
                    </SignedOut>
                </div>
            </div>
        </ModalBase>
    );
};

export default CreatePackCopyAutomaticModal;
